import { LineChart } from "@/components/charts/line-chart";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useBrandColor } from "@/lib/context/brand-color-context";
import {
  useCustomInstrument,
  useCustomInstrumentStatistics,
} from "@/lib/hooks/use-custom-instruments";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./custom-instrument-chart-dialog.i18n";

type CustomInstrumentChartDialogProps = {
  open: boolean;
  ticker: string | null;
  currency: string | null;
  onClose: () => void;
};

export function CustomInstrumentChartDialog({
  open,
  ticker,
  currency,
  onClose,
}: CustomInstrumentChartDialogProps) {
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const brandColor = useBrandColor();
  const { instrument, isLoading } = useCustomInstrument(open ? ticker : null);
  const { stats } = useCustomInstrumentStatistics(open ? ticker : null, currency);

  const formatPct = (value: number) => (Number.isNaN(value) ? "N/A" : `${value.toFixed(2)}%`);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl">
        {/* Header is always rendered so DialogContent always has a Title/Description
            (Radix a11y requirement) — even during the loading state. */}
        <DialogHeader>
          <DialogTitle>{instrument?.name ?? ticker ?? ""}</DialogTitle>
          <DialogDescription>{instrument?.ticker ?? ticker ?? ""}</DialogDescription>
        </DialogHeader>

        {isLoading || !instrument?.data?.length ? (
          <div className="py-8 text-center text-muted-foreground">{t.loading}</div>
        ) : (
          <>
            <div className="flex gap-6 text-sm">
              <div>
                <span className="text-muted-foreground">{t.expectedReturn}: </span>
                <span className="font-medium">
                  {stats ? formatPct(stats.expectedReturn) : t.na}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">{t.expectedVolatility}: </span>
                <span className="font-medium">
                  {stats ? formatPct(stats.expectedVolatility) : t.na}
                </span>
              </div>
            </div>

            <LineChart
              data={instrument.data}
              width="container"
              height={300}
              lang={userLanguage}
              primaryColor={brandColor ?? undefined}
            />

            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
              <span>
                {t.firstDataPoint}: {instrument.data[0].date} ({instrument.data[0].value.toFixed(2)}
                )
              </span>
              <span>
                {t.lastDataPoint}: {instrument.data[instrument.data.length - 1].date} (
                {instrument.data[instrument.data.length - 1].value.toFixed(2)})
              </span>
              <span>
                {t.currency}: {instrument.currency}
              </span>
              <span>
                {t.dataPoints}: {instrument.data.length}
              </span>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
